import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, TriangleAlert, UserRound, Wallet } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Tabs, Toggle } from '@/components/ui/primitives'
import { CustomerStatement } from '@/pages/customers/CustomerStatement'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'
import { Pager } from '@/components/ListControls'
import { usePagedList } from '@/lib/paging'

export interface Customer {
  id: number
  name: string
  phone: string | null
  email: string | null
  creditLimitUsd: number
  balanceUsd: number
  availableCreditUsd: number
  isActive: boolean
  isOverLimit: boolean
}

interface Receivables {
  totalOutstandingUsd: number
  customerCount: number
  buckets: { label: string; amountUsd: number; customerCount: number }[]
  customers: Customer[]
}

type TabValue = 'all' | 'owing'

export function CustomersPage() {
  const queryClient = useQueryClient()
  const can = useSession((state) => state.can)
  // What is owed rides on ManageCustomers, not ViewReports. Whoever sets a customer's credit
  // limit is the person who needs to see it being used, and gating it behind reports meant
  // handing them the shop's takings and margins to show them one balance.
  const canManage = can(Permission.ManageCustomers)

  const [tab, setTab] = useState<TabValue>('all')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Customer | null>(null)
  const [isEditorOpen, setEditorOpen] = useState(false)
  const [statementFor, setStatementFor] = useState<Customer | null>(null)

  const customers = usePagedList<Customer>(['customers'], '/api/customers', { search })

  const receivables = useQuery({
    queryKey: ['receivables'],
    queryFn: () => apiClient.get<Receivables>('/api/customers/receivables'),
    enabled: canManage,
  })

  return (
    <div className="pb-page mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('Customers')}</h1>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            {t('Accounts, credit limits and what each customer owes.')}
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => {
              setEditing(null)
              setEditorOpen(true)
            }}
          >
            <Plus className="h-4 w-4" />
            {t('New customer')}
          </Button>
        )}
      </header>

      {canManage && receivables.data && receivables.data.totalOutstandingUsd > 0 && (
        <div className="surface-raised mb-5 rounded-[var(--radius-card)] border p-4">
          <p className="text-sm text-[var(--text-secondary)]">{t('Owed to the shop')}</p>
          <p className="tabular mt-1 text-2xl font-semibold">
            {formatUsd(receivables.data.totalOutstandingUsd)}
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            across {receivables.data.customerCount}{' '}
            {receivables.data.customerCount === 1 ? 'account' : 'accounts'}
          </p>

          <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-[var(--border-subtle)] pt-3 sm:grid-cols-4">
            {receivables.data.buckets.map((bucket, index) => (
              <div key={bucket.label}>
                <dt className="text-xs text-[var(--text-secondary)]">{bucket.label}</dt>
                {/* The oldest bucket is the one that signals a debt going bad. */}
                <dd
                  className={`tabular text-sm font-medium ${
                    index === receivables.data!.buckets.length - 1 && bucket.amountUsd > 0
                      ? 'text-danger-500'
                      : ''
                  }`}
                >
                  {formatUsd(bucket.amountUsd)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {canManage && (
        <div className="mb-4">
          <Tabs
            tabs={[
              { value: 'all' as const, label: t('All customers') },
              { value: 'owing' as const, label: t('Owing'), count: receivables.data?.customerCount },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
      )}

      {tab === 'all' && (
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('Search by name or phone')}
          leading={<Search className="h-4 w-4" />}
          wrapperClassName="mb-4"
          autoComplete="off"
        />
      )}

      <CustomerList
        customers={tab === 'owing' ? (receivables.data?.customers ?? []) : customers.page}
        emptyLabel={tab === 'owing' ? 'Nobody owes anything' : search ? 'Nothing matched' : t('No customers yet')}
        emptyHint={
          tab === 'owing'
            ? 'Accounts with a balance appear here, aged by how long they have been outstanding.'
            : 'Add a customer to let them buy on account.'
        }
        onOpen={(customer) => setStatementFor(customer)}
        onEdit={
          canManage
            ? (customer) => {
                setEditing(customer)
                setEditorOpen(true)
              }
            : undefined
        }
      />

      {/* Only on the browsable tab. "Owing" is the receivables report — it is already the whole
          answer, and paging it would suggest there is more owed than it is showing. */}
      {tab === 'all' && (
        <Pager list={customers} />
      )}

      <CustomerEditor
        open={isEditorOpen}
        customer={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['customers'] })
          await queryClient.invalidateQueries({ queryKey: ['receivables'] })
        }}
      />

      <CustomerStatement customer={statementFor} onClose={() => setStatementFor(null)} />
    </div>
  )
}

function CustomerList({
  customers,
  emptyLabel,
  emptyHint,
  onOpen,
  onEdit,
}: {
  customers: Customer[]
  emptyLabel: string
  emptyHint: string
  onOpen: (customer: Customer) => void
  onEdit?: (customer: Customer) => void
}) {
  if (customers.length === 0) {
    return <EmptyState icon={UserRound} title={emptyLabel} description={emptyHint} />
  }

  return (
    <ul className="flex flex-col gap-2">
      {customers.map((customer) => (
        <li
          key={customer.id}
          className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
        >
          <button type="button" onClick={() => onOpen(customer)} className="min-w-0 flex-1 text-left">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate font-medium">{customer.name}</span>
              {customer.isOverLimit && <Badge tone="danger">{t('Over limit')}</Badge>}
              {!customer.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
            </div>
            <p className="tabular truncate text-xs text-[var(--text-secondary)]">
              {customer.phone ?? t('No phone')} · {t('Limit')}{' '}
              <bdi className="tabular">{formatUsd(customer.creditLimitUsd)}</bdi>
            </p>
          </button>

          <div className="shrink-0 text-right">
            <p
              className={`tabular font-semibold ${
                customer.balanceUsd > 0 ? 'text-warn-500' : 'text-[var(--text-secondary)]'
              }`}
            >
              {formatUsd(customer.balanceUsd)}
            </p>
            <p className="text-xs text-[var(--text-secondary)]">{t('Owed')}</p>
          </div>

          {onEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit(customer)}>
              {t('Edit')}
            </Button>
          )}
        </li>
      ))}
    </ul>
  )
}

function CustomerEditor({
  open,
  customer,
  onClose,
  onSaved,
}: {
  open: boolean
  customer: Customer | null
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [limit, setLimit] = useState('0')
  const [isActive, setIsActive] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [seeded, setSeeded] = useState<number | null>(null)

  // Seeded from the record the first time it opens for that customer.
  if (open && seeded !== (customer?.id ?? 0)) {
    setSeeded(customer?.id ?? 0)
    setName(customer?.name ?? '')
    setPhone(customer?.phone ?? '')
    setEmail(customer?.email ?? '')
    setLimit(String(customer?.creditLimitUsd ?? 0))
    setIsActive(customer?.isActive ?? true)
    setError(null)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        address: null,
        taxNumber: null,
        creditLimitUsd: Number(limit || 0),
        isActive,
        notes: null,
      }
      return customer
        ? apiClient.put(`/api/customers/${customer.id}`, body)
        : apiClient.post('/api/customers', body)
    },
    onSuccess: async () => {
      await onSaved()
      setSeeded(null)
      onClose()
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  return (
    <Modal
      open={open}
      onClose={() => {
        setSeeded(null)
        onClose()
      }}
      title={customer ? `Edit ${customer.name}` : 'New customer'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label={t('Name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Input
          label={t('Phone')}
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="tabular"
          hint={t('Cashiers look people up by phone at the counter')}
        />
        <Input label={t('Email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input
          label={t('Credit limit (USD)')}
          type="number"
          inputMode="decimal"
          step="1"
          min="0"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          className="tabular"
          hint={t('Zero means cash only — they cannot buy on account at all')}
        />
        <Toggle checked={isActive} onChange={setIsActive} label={t('Active')} />

        {error && (
          <p role="alert" className="rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            <TriangleAlert className="mr-1 inline h-4 w-4" />
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}

export { Wallet }
